import { h } from 'preact';
import { useLocalization, useLocalizationState } from '@js/hooks/useLocalization';
import { SoldOut, ProductCardPrice } from './ProductCardPrice';
import { dateShort as formatDate } from '../../utils/format';

const parseIntDate = (date) => {
  const jst = new Date(date).toLocaleDateString('en-US', { timeZone: 'Asia/Tokyo' });
  return new Date(jst).getTime() / 1000;
};

const isPreOrder = (product) => {
  return Date.now() / 1000 < parseIntDate(product.release_date?.value);
};

const isTCGSingle = (product) => {
  return product.tags.includes('meta-trading cards-Singles');
};

const price = ({ price }) => {
  return Number(price.amount);
};

const listingOrFixedListing = (listing, fixed) => {
  if (fixed && fixed.availableForSale) {
    return fixed;
  }

  return listing;
};

const mapVariants = (product) => {
  let excl = null;
  let eb = null;
  let so = null;
  let n = null;
  let u = null;
  let fixed = null;

  product.variants.edges.forEach(({ node }) => {
    const title = node.title.toLowerCase();

    if (title.includes('special offer')) {
      so = node;
    } else {
      switch (title) {
        case 'brand new.':
          fixed = node;
          break;

        case 'shop exclusive':
          excl = node;
          break;

        case 'early bird':
          eb = node;
          break;

        case 'brand new':
        case 'unit':
        case title.includes('adapter'):
          n = node;
          break;

        case 'pre owned':
          u = node;
          break;

        default:
          break;
      }
    }
  });

  return {
    excl,
    eb,
    so,
    u,
    n: listingOrFixedListing(n, fixed),
  };
};

const PurchaseSelect = ({ variants }) => {
  return (
    <select data-product-select class="no-js" name="id">
      {variants.map((variant) => {
        if (!variant) return null;

        const variantId = variant.id.replace('gid://shopify/ProductVariant/', '');

        return <option key={variant.id} disabled={!variant.availableForSale} value={variantId} />;
      })}
    </select>
  );
};

const Container = ({ variants, children, purchase }) => {
  if (purchase) {
    return (
      <form
        accept-charset="UTF-8"
        action="/cart/add"
        class="tw-flex tw-w-full tw-gap-x-3"
        enctype="multipart/form-data"
        method="post"
      >
        <PurchaseSelect variants={variants} />

        {children}
      </form>
    );
  }

  return children;
};

const VariantsSoldOut = ({ product, purchase, variants }) => {
  const localization = useLocalizationState();
  const { t } = useLocalization('getLocale', localization);

  if (isPreOrder(product)) {
    return (
      <Container purchase={purchase} variants={[variants.excl || variants.eb || variants.n]}>
        <div class="product-label product-label--sold-out tw-relative !tw-flex tw-opacity-50">
          <span class="product-label__title">{t('products.pre_order')}</span>

          <span class="price">-</span>
        </div>

        <div class={`product-label product-label--release${purchase ? ' tw-w-full !tw-flex-auto' : ''}`}>
          <span class="product-label__title">{t('products.release_date')}</span>
          <span class="product-label__detail">{formatDate(product.release_date?.value)}</span>
        </div>
      </Container>
    );
  }

  if (variants.so) {
    return (
      <Container purchase={purchase} variants={[variants.so, variants.u]}>
        <SoldOut type="special-offer" variant={variants.so} />
        <SoldOut type="pre-owned" variant={variants.u} />
      </Container>
    );
  }

  return (
    <Container purchase={purchase} variants={[variants.n, variants.u]}>
      <SoldOut type="brand-new" variant={variants.n} />
      <SoldOut type="pre-owned" variant={variants.u} />
    </Container>
  );
};

const ProductCardPriceUsed = ({ variant, compare, purchase }) => {
  if (!variant || !variant.availableForSale) return null;
  if (compare && price(compare) > 0 && price(variant) >= price(compare)) return null;

  return (
    <ProductCardPrice
      msrp={compare ? Math.round(price(compare) * 100) : 0}
      purchase={purchase}
      type="pre-owned"
      variant={variant}
    />
  );
};

export default function ProductVariants({ product, purchase = false }) {
  const localization = useLocalizationState();
  const { t } = useLocalization('getLocale', localization);

  const variants = mapVariants(product);

  const isSoldOut = product.variants.edges.every(({ node }) => !node.availableForSale || price(node) === 0);
  if (isSoldOut) {
    return <VariantsSoldOut {...{ product, purchase, variants }} />;
  }

  const conversionRate = Number(METAFIELDS.conversionRates.USD);
  const msrp = Math.round(product.msrp?.value * conversionRate * 100);

  if (isPreOrder(product)) {
    return (
      <Container purchase={purchase} variants={[variants.eb || variants.n]}>
        <ProductCardPrice
          msrp={msrp}
          purchase={purchase}
          type="pre-order"
          variant={variants.excl || variants.eb || variants.n}
        />

        <div class={`product-label product-label--release${purchase ? ' tw-w-full !tw-flex-auto' : ''}`}>
          <span class="product-label__title">{t('products.release_date')}</span>
          <span class="product-label__detail">{formatDate(product.release_date?.value)}</span>
        </div>
      </Container>
    );
  }

  if (variants.so && variants.so.availableForSale) {
    return (
      <Container purchase={purchase} variants={[variants.so, variants.n, variants.u]}>
        <ProductCardPrice msrp={msrp} purchase={purchase} type="special-offer" variant={variants.so} />

        <ProductCardPriceUsed compare={variants.n} msrp={msrp} purchase={purchase} variant={variants.u} />
      </Container>
    );
  }

  // COMBAK: refactor variants logic to be more dynamic. (Lorenzo ~ 2023-06-16)
  if (isTCGSingle(product) && variants.excl) {
    return (
      <Container purchase={purchase} variants={[variants.excl]}>
        <ProductCardPriceUsed msrp={msrp} purchase={purchase} variant={variants.excl} />
      </Container>
    );
  }

  return (
    <Container purchase={purchase} variants={[variants.n, variants.u]}>
      {variants.n && <ProductCardPrice msrp={msrp} purchase={purchase} type="brand-new" variant={variants.n} />}

      <ProductCardPriceUsed compare={variants.n} msrp={msrp} purchase={purchase} variant={variants.u} />
    </Container>
  );
}
